const parse = (response) => {
  if (!response.ok) {
    return response.json().then((data) => {
      throw new Error(data.error || 'Request failed');
    });
  }

  return response.json();
};

// site_ids and countries are repeated rather than joined, because Rails parses the bracketed names
// into arrays and a comma-joined string would arrive as a single meaningless value. Shared by every
// metric card so each one sends the same Country/Site + Date Range filters the page bar holds.
export const buildProgramInsightsQuery = ({ countries, siteIds, dateRange } = {}) => {
  const query = new URLSearchParams();

  (countries || []).forEach((country) => query.append('countries[]', country));
  (siteIds || []).forEach((siteId) => query.append('site_ids[]', siteId));

  if (dateRange) {
    query.set('date_range', dateRange);
  }

  return query.toString();
};

// Backs the shared filter bar: the countries and sites the viewer may filter by, and the
// data-refresh banner shown above the page.
export const getProgramInsightsFilters = ({ signal } = {}) =>
  fetch('/api/v2/clinical/program_insights/filters', {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);

// Monthly unique sign-ups split by pre-/post-consent mentor engagement. The page filters are passed
// through so the card re-filters with the rest of the dashboard.
export const getProgramEngagement = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/program_engagement?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Journey outcomes for pre-consent participants (recruitment-to-enrollment transition).
export const getEngagementContinuation = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/engagement_continuation?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Drill-in behind Engagement Continuation: why pre-consent participants decided not to continue.
export const getConsentDecisionInsights = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/consent_decision_insights?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Average mentor satisfaction (1-5) overall and by consent stage, with distribution and by-country.
export const getOverallMentorSatisfaction = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/overall_mentor_satisfaction?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Favorable-response rates for the four value-of-mentorship questions, by consent stage.
export const getProgramSurveyResults = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/program_survey_results?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Participant status funnel: cumulative counts per program stage and drop-off between stages, with
// total / pre-consent / post-consent versions.
export const getParticipantProgramJourney = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/participant_program_journey?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};

// Participant demographics (age) and motivations for joining, split total / pre-consent / post-consent.
export const getOnboardingProfile = (filters, { signal } = {}) => {
  const query = buildProgramInsightsQuery(filters);

  return fetch(`/api/v2/clinical/program_insights/onboarding_profile?${query}`, {
    headers: { 'Content-Type': 'application/json' },
    signal,
  }).then(parse);
};
